<template>
    <WidgetSection
        ref="WidgetSection"
        :loaded="loaded"
        :loading="loading"
        :unavailable="unavailable"
        :defaultToggle="defaultToggle"
        @loading-start="handleLoadingStart"
        loadingText="common.loading_1"
        :paywall="paywallActive"
        :cssClass="cssClassWidgetSection + ' content-type-41'"
    >
        <template v-slot:section-title>
            <div class="title">
                {{ $t('components.prospect_ai.models.' + slug)}}
            </div>
        </template>

        <template v-slot:section-content>
            <div class="row row-content">
                <div v-if="data && slug === 'summary'" class="col-12 col-sm-10 summary" :class="narrowVersion ? 'col-md-12 col-lg-12 col-xl-10' : ' col-md-8 col-lg-6'">
                    <p>{{ data[0] }}</p>
                </div>
                <template v-else-if="data && slug === 'fundamentals'">
                    <ProspectAiFundamentals :data="data" />
                </template>
                <template v-else-if="data">
                    <ProspectAiHelper :data="data" />
                </template>
            </div>
        </template>
    </WidgetSection>
</template>

<script>
    import i18n from "@/plugins/i18n";
    import { useProspectStore } from '@/stores/prospect';
    import { useAccountStore } from '@/stores/account';
    import { fetchUrl } from '@/services/ppApi';
    import WidgetSection from '@/components/sections/WidgetSection';
    import ProspectAiHelper from '@/components/prospect/widgets/ProspectAiHelper';
    import ProspectAiFundamentals from '@/components/prospect/widgets/ProspectAiFundamentals';
    import { useToastStore } from '@/stores/utils/toast';

    export default {
        name: "ProspectAiWidget",
        components: {
            WidgetSection,
            ProspectAiHelper,
            ProspectAiFundamentals
        },
        props: {
            slug: {
                type: String,
                required: true
            },
            auto: {
                type: Boolean,
                default: false
            },
            defaultToggle: {
                type: Boolean,
                default: false
            },
            paywall: {
                type: Boolean,
                default: false
            },
            narrowVersion: {
                type: Boolean,
                default: false
            },
            cssClassWidgetSection: {
                type: String,
                required: false
            },
            showLoadingToasts: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                loading: false,
                loaded: false,
                abortController: null,
                timeoutId: null,
                isActive: true
            }
        },
        computed: {
            cheapmium() {
                return useAccountStore().product.includes('_pch');
            },
            paywallActive() {
                return this.paywall && (useAccountStore().paywall || this.cheapmium);
            },
            ai() {
                return useProspectStore().ai;
            },
            slugLocalized() {
                return this.slug + '_' + this.currentLocale;
            },
            modelLocalized() {
                return this.ai[this.slugLocalized] || false;
            },
            model() {
                return this.modelLocalized || this.ai[this.slug] || false;
            },
            unavailable() {
                return !this.slug || !this.model || (!this.data && this.loaded);
            },
            data() {
                switch (this.slug) {
                    case 'summary' :
                        return this.model?.details?.description?.length > 0 ? this.model.details.description : false;
                    default :
                        return this.model?.details ? this.model.details : false;
                }
            },
            currentLocale() {
                return i18n.global.locale;
            }
        },
        methods: {
            deleteModelOption() {
                useProspectStore().deleteAiOption(this.modelLocalized ? this.slugLocalized :this.slug);
            },
            async fetchAiModel() {
                if (!this.model || this.loaded) {
                    return;
                }

                if (this.abortController) {
                    this.abortController.abort();
                    this.abortController = null;
                }

                this.abortController = new AbortController();
                this.loading = true;

                try {
                    const response = await fetchUrl(this.model.api_urls[0], this.abortController.signal);

                    if(response?.data?.summary && (response.data?.summary.length > 0 || Object.keys(response.data.summary).length > 0)) {
                        this.model.details = response.data.summary;
                        this.loaded = true;

                        if(!this.showLoadingToasts && this.isActive) {
                            useToastStore().addToast(i18n.global.t(`toasts.success.ai_${this.slug}`), 'success');
                        }
                    } else {
                        this.deleteModelOption();

                        if(!this.showLoadingToasts && this.isActive) {
                            useToastStore().addToast(i18n.global.t(`toasts.error.ai_${this.slug}`), 'error');
                        }
                    }
                } catch {
                    this.deleteModelOption();

                    if(!this.showLoadingToasts && this.isActive) {
                        useToastStore().addToast(i18n.global.t(`toasts.error.ai_${this.slug}`), 'error');
                    }
                } finally {
                    this.loaded = true;
                    this.loading = false;
                    this.abortController = null;
                }
            },
            async handleLoadingStart() {
                if(!this.paywallActive && !this.loaded && !!this.model) {
                    await this.fetchAiModel();
                    if(!this.unavailable && this.$refs.WidgetSection) {
                        this.$refs.WidgetSection.toggleSection();
                    }
                }
            }
        },
        watch: {
            currentLocale() {
                if(this.loaded && this.model?.slug && this.model.slug.includes('_') && !this.data) {
                    this.$refs.WidgetSection.toggleSection();
                    this.loaded = false;
                    this.handleLoadingStart();
                }
            }
        },
        mounted() {
            if(!this.paywallActive && !this.loaded && (this.auto || this.defaultToggle) && !!this.model) {
                this.loading = true;
                this.timeoutId = setTimeout(() => {
                    this.fetchAiModel();
                }, 300);
            }
        },
        unmounted() {
            this.isActive = false;
            if (this.abortController) {
                this.abortController.abort();
                this.abortController = null;
            }
            if (this.timeoutId) {
                clearTimeout(this.timeoutId);
            }
        }
    }
</script>